import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { InitialLoadAction } from './initial-load-action';
import { LoadingService } from './loading-service';
import { BootstrapService } from './bootstrap.service';
import { ApplicationConfigService } from '../application-config.service';

/** Replaces InitConfigurationAction + InitNavigationRulesAction with a
 *  single roundtrip to /bootstrap. The server has already resolved which
 *  routes the user can access, so the SPA no longer downloads + evaluates
 *  the rule map locally — it just stores a Set<route> for guards to query. */
@Injectable({ providedIn: 'root' })
export class InitBootstrapAction extends InitialLoadAction<boolean> {

  constructor(
    loadingService: LoadingService,
    private bootstrapService: BootstrapService,
    private appConfig: ApplicationConfigService,
  ) {
    super('Bootstrap', loadingService);
  }

  override async onLoad(): Promise<boolean> {
    try {
      const payload = await firstValueFrom(this.bootstrapService.load());

      const cfg = payload.uiConfig;
      if (cfg) {
        // Same localStorage hydration the old InitConfigurationAction did
        // — kept identical so dependent code (paginators, idle timer,
        // refresh-token timer, feature flags) stays oblivious to the
        // upstream refactor.
        localStorage.setItem('paginator.page.size.options',  cfg.paginatorConfig.pageSizeOptions.toString());
        localStorage.setItem('paginator.default.page.size',  cfg.paginatorConfig.pageDefaultPageSize.toString());
        localStorage.setItem('paginator.show.first.last',    cfg.paginatorConfig.showFirstAndLast.toString());
        localStorage.setItem('paginator.hide.page.options',  cfg.paginatorConfig.hidePagesOption.toString());
        localStorage.setItem('session.timeout',              cfg.sessionTimeout.toString());
        localStorage.setItem('session.idle',                 cfg.sessionIdle.toString());
        localStorage.setItem('crud.stay.in.page.after.save', cfg.crudStayInPageAfterSave.toString());
        localStorage.setItem('refresh.token.before.exipation.ms', cfg.refreshTokenBeforeExpirationMs.toString());
        localStorage.setItem('version',                      cfg.version);
        const features = cfg.features ?? {};
        Object.keys(features).forEach(k => {
          localStorage.setItem(`feature.${k}`, String(features[k]));
        });
      }

      this.appConfig.setKnownRoutes(payload.knownRoutes ?? []);
      this.appConfig.setPermittedRoutes(payload.permittedRoutes ?? []);
    } catch (e) {
      console.error('[Bootstrap] load failed (non-fatal):', e);
    }
    return true;
  }
}
